import { Connection, Keypair, TransactionInstruction, VersionedTransactionResponse, BlockResponse } from '@solana/web3.js';
import { createUpdateMinimumFeeInstruction } from './utils/admin';
import { encapsulateKyberSecret } from './utils/crypto/encapsulateKyberSecret';
import { deriveKyberSecret } from './utils/crypto/deriveKyberSecret';
import { deriveFilterTag } from './utils/crypto/deriveFilterTag';
import { deriveFilterTags } from './utils/crypto/deriveFilterTags';
import { deriveRandomFilterTag } from './utils/crypto/deriveRandomFilterTag';
import { deriveKyberPublicKeyHash } from './utils/crypto/deriveKyberPublicKeyHash';
import { generateRandomConversationId as generateRandomConversationIdFn } from './utils/crypto/generateRandomConversationId';
import { generateDeterministicConversationId } from './utils/crypto/generateDeterministicConversationId';
import { generateUnlinkKeypair } from './utils/crypto/generateUnlinkKeypair';
import { parseFilterTag } from './utils/crypto/parseFilterTag';
import { matchesSharedSecretHint } from './utils/crypto/matchesSharedSecretHint';
import { createChunkedSendInstructions } from './utils/transmission/createChunkedSendInstructions';
import { parseChunkedDataFromTransaction } from './utils/transmission/parseChunkedDataFromTransaction';
import { parseKnownPayloadFromGroup } from './utils/transmission/parseKnownPayloadFromGroup';
import { storeAndGroupFragments } from './utils/fragments/storeAndGroupFragments';
import { MessageFragment } from './types/messageFragment';
import { GroupedMessageResult } from './types/groupedMessageResult';
import { serializeMessageFragment } from './utils/serialization/serializeMessageFragment';
import { groupMessageFragmentsInPlace } from './utils/helpers/groupMessageFragmentsInPlace';
import { splitPayloadIntoFragments } from './utils/helpers/splitPayloadIntoFragments';
import { toHex } from './utils/helpers/toHex';
import { txIncludesUnlinkProgram } from './utils/helpers/TxIncludesUnlinkProgram';
import { MESSAGE_TYPE_TEXT, MESSAGE_TYPE_HANDSHAKE_REQUEST, MESSAGE_TYPE_HANDSHAKE_CONFIRM } from './constants/message';
import { UNLINK_PROGRAM_ID } from './constants/general';
import { FragmentStoreAdapter } from './types/fragmentStoreAdapter';
import { handshakeConfirmPayload } from './types/handshakeConfirmPayload';
import { deserializeHandshakeRequestPayload } from './utils/serialization/deserializeHandshakeRequestPayload';
import { serializeHandshakeRequestPayload } from './utils/serialization/serializeHandshakeRequestPayload';
import { serializeHandshakeConfirmPayload } from './utils/serialization/serializeHandshakeConfirmPayload';
import { serializeMessagePayload } from './utils/serialization/serializeMessagePayload';
import { SenderHmacProofInput } from "./types/crypto/senderHmacProofInput";
import { uint8ArraysEqual } from "./utils/helpers/compare";
import { VerifySenderHmacProofInput } from "./types/crypto/verifySenderHmacProofInput";

/**
 * UnlinkSDK
 *
 * This SDK provides message encryption, transmission, and decryption on Solana.
 * It abstracts the cryptographic and Solana RPC logic behind a simplified interface.
 */
export class UnlinkSDK {
  private connection: Connection; // RPC connection to a Solana node
  private fragmentStore: Map<string, MessageFragment[]> = new Map();
  private fragmentAdapter?: FragmentStoreAdapter;
  private payer?: Keypair; // Used to sign and pay for transactions

  /**
   * Initializes the Unlink SDK with the required Solana connection, payer identity,
   * and optional persistent fragment storage adapter.
   *
   * @param opts.connection - Solana RPC connection used to fetch and send transactions
   * @param opts.payer - Keypair responsible for signing and funding message transactions
   * @param opts.fragmentAdapter - (Optional) Adapter used to persist and restore message fragments 
   *                               across sessions (e.g., localStorage, file, or custom backend)
   */
  constructor(opts: { rpcUrl: string, fragmentAdapter?: FragmentStoreAdapter }) {
    this.connection = new Connection(opts.rpcUrl, 'confirmed');
    this.fragmentAdapter = opts.fragmentAdapter;
  }

  setPayer(opts: { payer: Keypair }) {
    if (
      !opts.payer ||
      !(opts.payer.publicKey) ||
      !(opts.payer.secretKey instanceof Uint8Array)
    ) {
      throw new Error("Invalid payer: expected a Solana Keypair instance");
    }

    this.payer = opts.payer;
  }

  /**
    * Initializes the in-memory fragment store by loading persisted message fragments,
    * typically from an external adapter (e.g., local storage, IndexedDB, custom backend).
    * 
    * This ensures previously seen message fragments are available for assembly
    * when new transactions are read, allowing recovery of partially received messages.
    *
    * If no adapter is configured or the result is invalid, initialization is skipped silently.
    */
  async initializeFragments() {
    if (!this.fragmentAdapter) return;

    const stored = await this.fragmentAdapter.loadFragments();

    if (!Array.isArray(stored)) return;

    for (const frag of stored) {
      const id = toHex(frag.messageId);
      if (!this.fragmentStore.has(id)) {
        this.fragmentStore.set(id, []);
      }
      this.fragmentStore.get(id)!.push(frag);
    }
  }

  async createHandshakeSendInstructions({
    message,
    receiverViewPublicKey,
    senderViewPublicKey,
    senderViewPrivateKey,
    senderNextViewPublicKey,
    sessionNextKyberPublicKey,
    spamMitigationFeeLamports,
    conversationId,
    kyberSecret,
    randomFill,
    createSenderHmacProof,
    filterTag,
  }: {
    message: string;
    receiverViewPublicKey: Uint8Array;
    sessionNextKyberPublicKey: Uint8Array;
    senderViewPublicKey: Uint8Array;
    senderViewPrivateKey: Uint8Array;
    senderNextViewPublicKey: Uint8Array;
    spamMitigationFeeLamports: bigint;
    conversationId: Uint8Array;
    kyberSecret?: Uint8Array;
    randomFill: (target: Uint8Array) => void;
    createSenderHmacProof: (input: SenderHmacProofInput) => Promise<Uint8Array>;
    filterTag: Uint8Array;
  }): Promise<{
    instructions: TransactionInstruction[];
    messageId: Uint8Array;
  }> {
    const payload = serializeHandshakeRequestPayload(
      sessionNextKyberPublicKey,
      message,
    );
    
    const { instructions, messageId } = await this.createDataSendInstructions({
      receiverViewPublicKey,
      senderViewPublicKey,
      senderViewPrivateKey,
      senderNextViewPublicKey,
      data: payload,
      messageType: MESSAGE_TYPE_HANDSHAKE_REQUEST,
      spamMitigationFeeLamports,
      conversationId,
      kyberSecret,
      randomFill,
      createSenderHmacProof,
      filterTag,
    });

    return {
      instructions,
      messageId,
    }
  }

  /**
   * Confirms a handshake by generating a Kyber ciphertext and sending it
   * to the recipient, allowing them to derive the shared post-quantum secret.
   *
   * This message proves sender authenticity and provides the encapsulated key.
   *
   * @param receiverViewPublicKey - Recipient's view key used for encryption
   * @param senderViewPublicKey - Sender's current view key (identity)
   * @param senderViewPrivateKey - Sender's view private key used for HMAC proof
   * @param senderNextViewPublicKey - Optional next view key (for rotation)
   * @param sessionKyberPublicKey - Session Kyber public key for encapsulation
   * @param spamMitigationFeeLamports - Lamports paid to avoid spam
   * @param conversationId - 16-byte context identifier binding the message
   */
  async createHandshakeConfirmInstructions({
    receiverViewPublicKey,
    senderViewPublicKey,
    senderViewPrivateKey,
    senderNextViewPublicKey,
    sessionKyberPublicKey,
    spamMitigationFeeLamports,
    conversationId,
    kyberSecret,
    randomFill,
    createSenderHmacProof,
    filterTag,
  }: { 
    receiverViewPublicKey: Uint8Array;
    senderViewPublicKey: Uint8Array;
    senderViewPrivateKey: Uint8Array;
    senderNextViewPublicKey: Uint8Array;
    sessionKyberPublicKey: Uint8Array;
    spamMitigationFeeLamports: bigint;
    conversationId: Uint8Array;
    kyberSecret?: Uint8Array;
    randomFill: (target: Uint8Array) => void;
    createSenderHmacProof: (input: SenderHmacProofInput) => Promise<Uint8Array>;
    filterTag: Uint8Array;
  }): Promise<{
    instructions: TransactionInstruction[];
    messageId: Uint8Array;
    kyberSecret: Uint8Array;
    kyberCiphertext: Uint8Array;
  }> {
    // === Generate the Kyber encapsulated ciphertext ===
    const { 
      ciphertext: newKyberCiphertext, 
      sharedSecret: newKyberSecret, 
    } = await encapsulateKyberSecret(sessionKyberPublicKey);

    const payload = await serializeHandshakeConfirmPayload(newKyberCiphertext, sessionKyberPublicKey);

    const { instructions, messageId } = await this.createDataSendInstructions({
      receiverViewPublicKey,
      senderViewPublicKey,
      senderViewPrivateKey,
      senderNextViewPublicKey,
      data: payload,
      messageType: MESSAGE_TYPE_HANDSHAKE_CONFIRM,
      spamMitigationFeeLamports,
      conversationId,
      kyberSecret,
      randomFill,
      createSenderHmacProof,
      filterTag,
    });

    return {
      instructions,
      messageId,
      kyberSecret: newKyberSecret,
      kyberCiphertext: newKyberCiphertext,
    };
  }

  /**
   * Encrypts and sends a plain text message to a recipient's view public key.
   * Internally calls `createDataSendInstructions()` with UTF-8 encoded content and standard message type.
   *
   * @param receiverViewPublicKey - Recipient's public view key for encryption
   * @param senderViewPublicKey - Sender's current view public key (long-term identity)
   * @param senderViewPrivateKey - Sender's private key for HMAC proof generation
   * @param senderNextViewPublicKey - Sender's next view key (optional rotation)
   * @param plaintext - Message string to be encoded and sent
   * @param spamMitigationFeeLamports - Fee in lamports to mitigate spam
   * @param conversationId - Thread identifier used to group messages
   */
  async createMessageSendInstructions({
    receiverViewPublicKey,
    senderViewPublicKey,
    senderViewPrivateKey,
    senderNextViewPublicKey,
    plaintext,
    spamMitigationFeeLamports,
    conversationId,
    kyberSecret,
    randomFill,
    createSenderHmacProof,
    filterTag,
  }: {
    receiverViewPublicKey: Uint8Array;
    senderViewPublicKey: Uint8Array;
    senderViewPrivateKey: Uint8Array;
    senderNextViewPublicKey: Uint8Array;
    plaintext: string;
    spamMitigationFeeLamports: bigint;
    conversationId: Uint8Array;
    kyberSecret: Uint8Array;
    randomFill: (target: Uint8Array) => void;
    createSenderHmacProof: (input: SenderHmacProofInput) => Promise<Uint8Array>;
    filterTag: Uint8Array;
  }): Promise<{
    instructions: TransactionInstruction[];
    messageId: Uint8Array;
  }> {
    const payload = serializeMessagePayload(
      plaintext,
    );

   const { instructions, messageId } = await this.createDataSendInstructions({
      receiverViewPublicKey,
      senderViewPublicKey,
      senderViewPrivateKey,
      senderNextViewPublicKey,
      data: payload,
      messageType: MESSAGE_TYPE_TEXT,
      spamMitigationFeeLamports,
      conversationId,
      kyberSecret,
      randomFill,
      createSenderHmacProof,
      filterTag,
    });

   return {
    instructions,
    messageId,
   }
  }

 /**
   * Encrypts and sends raw binary data to a recipient using the Unlink protocol.
   * 
   * This method supports large payloads by fragmenting the message into multiple parts
   * that fit within the Solana transaction size constraints. Each fragment is 
   * serialized, encrypted, and submitted on-chain as an individual transaction.
   *
   * This function is suitable for sending structured or non-text binary messages,
   * such as invitations, metadata blobs, or custom protocol formats.
   *
   * @param receiverViewPublicKey - Recipient’s long-term public view key (used for ECDH encryption)
   * @param senderViewPublicKey - Sender’s current public view key (used for sender authentication)
   * @param senderViewPrivateKey - Sender’s private view key (used to generate HMAC proof)
   * @param senderNextViewPublicKey - Optional forward secrecy key (zero-filled if unused)
   * @param data - Raw binary payload to send; will be fragmented and serialized
   * @param messageType - Type identifier for the message (0 = plain text, ≥128 = custom format)
   * @param spamMitigationFeeLamports - Lamports paid to disincentivize spam; burned or collected
   * @param conversationId - 16-byte unique identifier for the conversation thread
   * @returns An array of Solana transaction signatures, one per fragment sent
   */
  async createDataSendInstructions({
    receiverViewPublicKey,
    senderViewPublicKey,
    senderViewPrivateKey,
    senderNextViewPublicKey,
    data,
    messageType,
    spamMitigationFeeLamports,
    conversationId,
    kyberSecret,
    randomFill,
    createSenderHmacProof,
    filterTag,
  }: {
    receiverViewPublicKey: Uint8Array;
    senderViewPublicKey: Uint8Array;
    senderViewPrivateKey: Uint8Array;
    senderNextViewPublicKey: Uint8Array;
    data: Uint8Array;
    messageType: number;
    spamMitigationFeeLamports: bigint;
    conversationId: Uint8Array;
    kyberSecret?: Uint8Array;
    randomFill: (target: Uint8Array) => void;
    createSenderHmacProof: (input: SenderHmacProofInput) => Promise<Uint8Array>;
    filterTag: Uint8Array;
  }): Promise<{
    instructions: TransactionInstruction[];
    messageId: Uint8Array;
  }> {

    const usesSharedSecret = ![
      MESSAGE_TYPE_HANDSHAKE_REQUEST,
      MESSAGE_TYPE_HANDSHAKE_CONFIRM,
    ].includes(messageType);

    if (usesSharedSecret && !kyberSecret) {
      throw new Error(
        `Message type ${messageType} requires a Kyber-derived shared secret to encrypt the sender's view key (R).`
      );
    }

    if (!this.payer) {
      throw new Error(
        `No payer set.`
      );
    }

    const { instructions, messageId } = await createChunkedSendInstructions({
      messagePayload: data,
      messageType,
      receiverViewPublicKey,
      senderViewPublicKey,
      senderViewPrivateKey,
      senderNextViewPublicKey,
      spamMitigationFeeLamports,
      conversationId,
      payer: this.payer,
      kyberSecret,
      randomFill,
      createSenderHmacProof,
      filterTag,
    });

    return { 
      instructions, 
      messageId, 
    };
  }

  async parseBlockForMessages({
    block,
    receiverViewKeypairs,
    sessionKyberKeypairs,
    verifySenderHmacProof,
  }: {
    block: BlockResponse;
    receiverViewKeypairs: {
      viewPublicKey: Uint8Array;
      viewPrivateKey: Uint8Array;
    }[];
    sessionKyberKeypairs?: {
      kyberPublicKey: Uint8Array;
      kyberPrivateKey: Uint8Array;
      kyberSecret: Uint8Array;
    }[];
    verifySenderHmacProof: (input: VerifySenderHmacProofInput) => Promise<boolean>;
  }) {
    if (!block.transactions || block.transactions.length === 0) {
      return [];
    }

    const parsed = await Promise.all(
      block.transactions.map(async (tx): Promise<any[]> => {

        const txWithSlot: VersionedTransactionResponse = {
          ...tx,
          slot: (block as unknown as { slot: number }).slot, // <-- coming from the outer BlockResponse
        };

        if (!txIncludesUnlinkProgram(txWithSlot)) return [];

        try {
          return await this.readMessages({
            transaction: tx as VersionedTransactionResponse,
            receiverViewKeypairs,
            sessionKyberKeypairs,
            verifySenderHmacProof,
          });
        } catch (_) {
          return []; // Swallow per-tx parsing failures
        }
      })
    );

    // Flatten results
    return parsed.flat();
  }

/**
 * Reads and decrypts messages from a given Solana transaction.
 *
 * Handles both ECDH and optional Kyber-based hybrid encryption.
 * Also supports deriving shared Kyber secrets if multiple keypairs are provided.
 *
 * @param transaction - The Solana transaction to inspect.
 * @param receiverViewPublicKey - Receiver’s (possibly ratcheted) public view key.
 * @param receiverViewPrivateKey - Receiver’s private view key to derive ECDH shared secret.
 * @param receiverKyberKeypairs - Optional array of Kyber keypairs to attempt handshake decryption.
 * @param verifySenderHmacProof - Function to verify sender authenticity via HMAC.
 * @returns Parsed, verified messages grouped by fragment and decorated with metadata.
 */
async readMessages({
  transaction,
  receiverViewKeypairs,
  sessionKyberKeypairs,
  verifySenderHmacProof,
}: {
  transaction: VersionedTransactionResponse;
  receiverViewKeypairs: {
    viewPublicKey: Uint8Array;
    viewPrivateKey: Uint8Array;
  }[];
  sessionKyberKeypairs?: {
    kyberPublicKey: Uint8Array;
    kyberPrivateKey: Uint8Array;
    kyberSecret: Uint8Array;
  }[];
  verifySenderHmacProof: (input: VerifySenderHmacProofInput) => Promise<boolean>;
}) {
  // Parse encrypted data payloads from the transaction
  const rawResults = await parseChunkedDataFromTransaction({
    transaction,
    receiverViewKeypairs,
    sessionKyberKeypairs,
    verifySenderHmacProof,
  });

  // Extract message fragments from results
  const newFragments = rawResults.map(r => r.unpackedMessage.message);

  // Group fragmented messages for reassembly
  const grouped = storeAndGroupFragments(this.fragmentStore, newFragments);

  // Persist updated fragment state if supported
  if (this.fragmentAdapter?.storeFragments) {
    const allFragments = [...this.fragmentStore.values()].flat();
    await this.fragmentAdapter.storeFragments(allFragments);
  }

  // Parse known message types and attach metadata
  const parsedMessages = await Promise.all(
    grouped.map(async (group) => {
      const parsed = parseKnownPayloadFromGroup(group);

      // Reuse fragment metadata (sender info, conversation ID, etc.)
      const anyRaw = rawResults.find(
        r => toHex(r.unpackedMessage.message.messageId) === group.messageId
      );
      if (!anyRaw) {
        throw new Error(`Fragment group ${group.messageId} has no matching raw result`);
      }

      const meta = anyRaw.unpackedMessage;

      // The receiver view key that successfully decrypted this message (identity or ratcheted)
      const receiverViewPublicKey = anyRaw.receiverViewPublicKey;

      // Sum feeLamports for all matching fragments
      const totalFeeLamports = rawResults
        .filter(r => toHex(r.unpackedMessage.message.messageId) === group.messageId)
        .reduce((sum, r) => sum + (r.unpackedMessage.feeLamports ?? 0n), 0n);

      let kyberKeys = null;

      // If this is a handshake confirm and we may need to derive the Kyber shared secret
      if (
        parsed.isParsed &&
        parsed.messageType === MESSAGE_TYPE_HANDSHAKE_CONFIRM &&
        sessionKyberKeypairs
      ) {
        const confirmPayload = parsed.parsed as handshakeConfirmPayload;

        // Try each keypair to see if the public key hash matches the one in the payload
        for (let i = 0; i < sessionKyberKeypairs.length; i++) {
          const keypair = sessionKyberKeypairs[i];    
          const candidateHash = deriveKyberPublicKeyHash({ kyberPublicKey: keypair.kyberPublicKey });
  
          if (uint8ArraysEqual(candidateHash, confirmPayload.kyberPublicKeyHash)) {
            const kyberSecret = await deriveKyberSecret({
              receiverKyberPrivateKey: keypair.kyberPrivateKey,
              ciphertext: confirmPayload.kyberCiphertext,
            });

            // Correct match — store secret with keypair
            kyberKeys = {
              kyberPublicKey: keypair.kyberPublicKey,
              kyberPrivateKey: keypair.kyberPrivateKey,
              kyberSecret,
            };
            break;
          }
        }

        if (!kyberKeys) {
          throw new Error("Failed to derive Kyber shared secret: no matching keypair found");
        }
      }

      return {
        ...parsed,
        kyberKeys,
        conversationId: meta.conversationId,
        receiverViewPublicKey,
        senderViewPublicKey: meta.senderViewPublicKey,
        senderNextViewPublicKey: meta.senderNextViewPublicKey,
        totalFeeLamports,
      };
    })
  );

  return parsedMessages;
}

  /**
   * Exposes keypair generation using NaCl box (Curve25519).
   * Useful for generating unlinkable, ephemeral keypairs.
   */
  generateRandomConversationId({ randomFill }: { randomFill: (target: Uint8Array) => void }): Uint8Array {
    return generateRandomConversationIdFn({ randomFill });
  }

  generateUnlinkKeypair = generateUnlinkKeypair;
  generateDeterministicConversationId = generateDeterministicConversationId;
  createUpdateMinimumFeeInstruction = createUpdateMinimumFeeInstruction;
  deriveFilterTag = deriveFilterTag;
  deriveFilterTags = deriveFilterTags;
  deriveRandomFilterTag = deriveRandomFilterTag;
  parseFilterTag = parseFilterTag;
  matchesSharedSecretHint = matchesSharedSecretHint;
  UNLINK_PROGRAM_ID = UNLINK_PROGRAM_ID;
}